import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Calendar, TrendingDown, AlertTriangle, Filter, Download, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Grid, List, Eye, BarChart3 } from 'lucide-react';
import { sportingService } from '../../services/sportingService';
import type { Player } from '../../types/player';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { translatePosition, translateFoot } from '../../utils/positionTranslations';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import 'rc-slider/assets/index.css';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import ContractCard from '../../components/Sporting/ContractCard';
import ContractStatsDashboard from '../../components/Sporting/ContractStatsDashboard';
import ModernFiltersPanel from '../../components/Sporting/ModernFiltersPanel';

// Extended Player interface for contract monitoring
interface PlayerWithContract extends Player {
  dias_restantes: number;
}

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

type SortField = 'nombre_transfermarkt' | 'posicion_transfermarkt' | 'equipo_transfermarkt' | 'competicion' | 'fecha_fin_contrato' | 'dias_restantes' | 'valor_mercado' | 'edad_transfermarkt';
type SortDirection = 'asc' | 'desc';

const ContractMonitorPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'es' | 'en';
  const [players, setPlayers] = useState<PlayerWithContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'this_season' | 'one_year' | 'long_term'>('all');
  const [positionFilter, setPositionFilter] = useState<string>('all');
  const [leagueFilter, setLeagueFilter] = useState<string>('all');
  const [showExpired, setShowExpired] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('fecha_fin_contrato');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [availableLeagues, setAvailableLeagues] = useState<string[]>([]);
  const [availablePositions, setAvailablePositions] = useState<string[]>([]);
  
  // View mode state
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [heightRange, setHeightRange] = useState<[number, number]>([1.50, 2.10]);

  
  // New filter states
  const [ageRange, setAgeRange] = useState<[number, number]>([15, 40]);
  // Market value filter states
  const [marketValueFilterType, setMarketValueFilterType] = useState<'all' | 'low' | 'high'>('low');
  const [marketValueRangeAll, setMarketValueRangeAll] = useState<[number, number]>([0, 200]);
  const [marketValueRangeLow, setMarketValueRangeLow] = useState<[number, number]>([0, 10]);
  const [marketValueRangeHigh, setMarketValueRangeHigh] = useState<[number, number]>([10, 200]);
  // New state for the "has reports" filter
  const [hasReportsFilter, setHasReportsFilter] = useState<boolean>(false);

  // Advanced filters state
  const [citizenshipFilter, setCitizenshipFilter] = useState<string>('');
  const [footFilter, setFootFilter] = useState<string>('all');
  const [isEuFilter, setIsEuFilter] = useState<boolean | null>(null);
  const [countryOfBirthFilter, setCountryOfBirthFilter] = useState<string>('');
  const [currentClubFilter, setCurrentClubFilter] = useState<string>('');
  const [contractExpiresFrom, setContractExpiresFrom] = useState<string>('');
  const [contractExpiresTo, setContractExpiresTo] = useState<string>('');
  const [playerAgentFilter, setPlayerAgentFilter] = useState<string>('');
  
  // Performance stats filters
  const [minGoals, setMinGoals] = useState<number>(0);
  const [minAssists, setMinAssists] = useState<number>(0);
  const [minMatchesPlayed, setMinMatchesPlayed] = useState<number>(0);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    // Always load contract data, but wait for initial data to be attempted first
    const timer = setTimeout(() => {
      loadContractData();
    }, 100);
    
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, sortField, sortDirection, filter, 
    positionFilter, leagueFilter, showExpired, searchTerm, ageRange, 
    marketValueFilterType, marketValueRangeAll, marketValueRangeLow, 
    marketValueRangeHigh, hasReportsFilter, heightRange,
    citizenshipFilter, footFilter, isEuFilter, countryOfBirthFilter,
    currentClubFilter, contractExpiresFrom, contractExpiresTo, playerAgentFilter,
    minGoals, minAssists, minMatchesPlayed]);

  const loadContractData = async () => {
    setLoading(true);
    try {
      // Build search query for contract monitoring
      let query = 'jugadores con fecha_fin_contrato';
      
      if (searchTerm) {
        query += ` nombre: ${searchTerm}`;
      }
      
      if (positionFilter !== 'all') {
        query += ` posicion: ${positionFilter}`;
      }
      
      if (leagueFilter !== 'all') {
        query += ` liga: ${leagueFilter}`;
      }
      
      if (filter !== 'all') {
        const currentDate = new Date();
        switch (filter) {
          case 'this_season':
            const endOfSeason = new Date(currentDate.getFullYear(), 5, 30);
            if (currentDate.getMonth() >= 6) {
              endOfSeason.setFullYear(currentDate.getFullYear() + 1);
            }
            query += ` contratos que vencen antes del ${endOfSeason.toISOString().split('T')[0]}`;
            break;
          case 'one_year':
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
            query += ` contratos que vencen antes del ${oneYearFromNow.toISOString().split('T')[0]}`;
            break;
          case 'long_term':
            const twoYearsFromNow = new Date();
            twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
            query += ` contratos que vencen despues del ${twoYearsFromNow.toISOString().split('T')[0]}`;
            break;
        }
      }
      
      if (ageRange[0] > 15 || ageRange[1] < 40) {
        query += ` edad entre ${ageRange[0]} y ${ageRange[1]}`;
      }
      
      // Add market value filter
      let minValue = 0;
      let maxValue = 200;
      
      if (marketValueFilterType === 'low') {
        minValue = marketValueRangeLow[0];
        maxValue = marketValueRangeLow[1];
      } else if (marketValueFilterType === 'high') {
        minValue = marketValueRangeHigh[0];
        maxValue = marketValueRangeHigh[1];
      } else if (marketValueFilterType === 'all') {
        minValue = marketValueRangeAll[0];
        maxValue = marketValueRangeAll[1];
      }
      
      if (minValue > 0 || maxValue < 200) {
        query += ` valor_mercado entre ${minValue * 1000000} y ${maxValue * 1000000}`;
      }
      
      // Add height filter
      if (heightRange[0] > 1.50 || heightRange[1] < 2.10) {
        query += ` altura entre ${heightRange[0]} y ${heightRange[1]}`;
      }
      
      // Add advanced filters
      if (citizenshipFilter) {
        query += ` nacionalidad: ${citizenshipFilter}`;
      }
      
      if (footFilter !== 'all') {
        query += ` pie: ${footFilter}`;
      }
      
      if (isEuFilter !== null) {
        query += ` ciudadano UE: ${isEuFilter ? 'sí' : 'no'}`;
      }
      
      if (countryOfBirthFilter) {
        query += ` país de nacimiento: ${countryOfBirthFilter}`;
      }
      
      if (currentClubFilter) {
        query += ` club: ${currentClubFilter}`;
      }
      
      if (contractExpiresFrom) {
        query += ` contrato vence después de ${contractExpiresFrom}`;
      }
      
      if (contractExpiresTo) {
        query += ` contrato vence antes de ${contractExpiresTo}`;
      }
      
      if (playerAgentFilter) {
        query += ` agente: ${playerAgentFilter}`;
      }
      
      // Add performance stats filters
      if (minGoals > 0) {
        query += ` mínimo ${minGoals} goles`;
      }
      
      if (minAssists > 0) {
        query += ` mínimo ${minAssists} asistencias`;
      }
      
      if (minMatchesPlayed > 0) {
        query += ` mínimo ${minMatchesPlayed} partidos`;
      }
      
      const response = await sportingService.searchPlayersAI(query, itemsPerPage, (currentPage - 1) * itemsPerPage);
      
      console.log('API Response:', response);
      console.log('Total jugadores recibidos:', response.jugadores?.length || 0);
      
      // Filter players with contract end dates and calculate days remaining
      const playersWithContracts: PlayerWithContract[] = response.jugadores
        .map(player => {
          // Check both new and legacy contract date fields
          const contractDate = player.contract_expires || player.fecha_fin_contrato;
          console.log('Player contract date:', player.player_name || player.nombre_transfermarkt, contractDate);
          
          let daysRemaining = 0;
          if (contractDate) {
            try {
              const contractEndDate = new Date(contractDate);
              const today = new Date();
              daysRemaining = Math.ceil((contractEndDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            } catch (error) {
              console.error('Error parsing date for player:', player.player_name || player.nombre_transfermarkt, error);
            }
          }
          
          // Normalize to use fecha_fin_contrato for consistency with existing code
          return {
            ...player,
            fecha_fin_contrato: contractDate || player.fecha_fin_contrato,
            dias_restantes: daysRemaining
          } as PlayerWithContract;
        })
        .filter(player => player.contract_expires || player.fecha_fin_contrato) // Filter after mapping to check both fields
        .sort((a, b) => {
          if (sortField === 'fecha_fin_contrato') {
            const dateA = new Date(a.fecha_fin_contrato!).getTime();
            const dateB = new Date(b.fecha_fin_contrato!).getTime();
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
          }
          return 0;
        });
      
      console.log('Players after filtering:', playersWithContracts.length);
      
      setPlayers(playersWithContracts);
      setTotalCount(response.total);
      setTotalPages(Math.ceil(response.total / itemsPerPage));
    } catch (error: any) {
      console.error('Error loading contract data:', error);
      toast.error('Error al cargar los datos de contratos');
      setPlayers([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    try {
      // Load all available positions and leagues from database
      const allPlayersResponse = await sportingService.searchPlayersAI('jugadores con fecha_fin_contrato', 1000, 0);
      
      // Extract unique positions (check both new and legacy field names)
      const positions = allPlayersResponse.jugadores
        .map(p => (p.position || p.posicion_transfermarkt)?.trim())
        .filter((pos): pos is string => pos !== undefined && pos.length > 0)
        .filter((pos, index, arr) => arr.indexOf(pos) === index)
        .sort();
      setAvailablePositions(positions);
      
      // Extract unique leagues
      const leagues = allPlayersResponse.jugadores
        .map(p => p.competicion)
        .filter(Boolean)
        .filter((league, index, arr) => arr.indexOf(league) === index)
        .sort();
      setAvailableLeagues(leagues as string[]);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  // Helper function to determine contract period
  const getContractPeriod = (dias: number) => {
    const currentDate = new Date();
    const endOfSeason = new Date(currentDate.getFullYear(), 5, 30); // June 30th
    if (currentDate.getMonth() >= 6) { // If we're past June, next season ends next year
      endOfSeason.setFullYear(currentDate.getFullYear() + 1);
    }
    
    const daysToEndOfSeason = Math.ceil((endOfSeason.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (dias < 0) return 'expired';
    if (dias <= daysToEndOfSeason) return 'this_season';
    if (dias <= 365) return 'one_year';
    return 'long_term';
  };

  const getUrgencyColor = (days: number) => {
    const period = getContractPeriod(days);
    switch (period) {
      case 'expired': return 'text-gray-600 bg-gray-100';
      case 'this_season': return 'text-red-600 bg-red-50';
      case 'one_year': return 'text-orange-600 bg-orange-50';
      case 'long_term': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getUrgencyIcon = (days: number) => {
    const period = getContractPeriod(days);
    switch (period) {
      case 'expired': return <TrendingDown className="h-4 w-4" />;
      case 'this_season': return <AlertTriangle className="h-4 w-4" />;
      case 'one_year': return <Clock className="h-4 w-4" />;
      case 'long_term': return <Calendar className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  const exportToCSV = () => {
    const headers = [
      t('contracts.name'), 
      'Edad',
      t('contracts.position'), 
      'Pie',
      'Altura',
      'Nacionalidad',
      t('contracts.team'), 
      t('contracts.league'), 
      t('contracts.contractEnd'), 
      t('contracts.daysRemaining'), 
      t('contracts.marketValue'),
      'Goles',
      'Asistencias',
      'Partidos',
      'Agente'
    ];
    const csvContent = [
      headers.join(','),
      ...players.map(player => {
        const playerName = player.player_name || player.nombre_transfermarkt || '';
        const playerAge = player.date_of_birth 
          ? new Date().getFullYear() - new Date(player.date_of_birth).getFullYear()
          : player.edad_transfermarkt || 0;
        const playerPosition = translatePosition(player.position || player.posicion_transfermarkt || '', currentLang);
        const playerFoot = translateFoot(player.foot || player.pie_habil || '', currentLang);
        const playerHeight = player.height || (player.altura ? `${player.altura} cm` : '');
        const playerNationality = player.citizenship || player.nacion || player.country_of_birth || '';
        const playerTeam = player.current_club_name || player.equipo_transfermarkt || '';
        const playerAgent = player.player_agent_name || player.player_agent || '';
        const contractDate = player.contract_expires || player.fecha_fin_contrato || '';
        
        return [
          `"${playerName}"`,
          playerAge,
          `"${playerPosition}"`,
          `"${playerFoot}"`,
          `"${playerHeight}"`,
          `"${playerNationality}"`,
          `"${playerTeam}"`,
          `"${player.competicion || ''}"`,
          `"${contractDate}"`,
          player.dias_restantes || 0,
          player.valor_mercado || 0,
          player.goles || 0,
          player.asistencias || 0,
          player.partidos_jugados || player.partidos || 0,
          `"${playerAgent}"`
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `contratos_expirando_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate summary stats based on all filtered data (not just current page)
  const summaryStats = useMemo(() => {
    // These stats should reflect the total filtered data, not just current page
    // We'll use the current page data as an approximation for now
    const thisSeason = players.filter((p) => getContractPeriod(p.dias_restantes) === 'this_season').length;
    const oneYear = players.filter((p) => getContractPeriod(p.dias_restantes) === 'one_year').length;
    const longTerm = players.filter((p) => getContractPeriod(p.dias_restantes) === 'long_term').length;
    const expired = players.filter((p) => p.dias_restantes < 0).length;
    const totalValue = players.reduce((sum, p) => sum + (p.valor_mercado || 0), 0);
    
    return { 
      thisSeason, 
      oneYear, 
      longTerm, 
      expired, 
      totalValue,
      totalPlayers: players.length
    };
  }, [players]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-indigo-700/20 backdrop-blur-3xl"></div>
        
        <div className="relative z-10 container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm mr-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-4xl font-bold text-white mb-2">Oportunidades del Mercado</h1>
                <p className="text-blue-100 text-lg">
                  Oportunidades de fichaje libre y gestión inteligente de contratos
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Dashboard */}
        <ContractStatsDashboard stats={summaryStats} />

        {/* Modern Filters Panel */}
        <ModernFiltersPanel
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filter={filter}
          setFilter={setFilter}
          positionFilter={positionFilter}
          setPositionFilter={setPositionFilter}
          leagueFilter={leagueFilter}
          setLeagueFilter={setLeagueFilter}
          ageRange={ageRange}
          setAgeRange={setAgeRange}
          heightRange={heightRange}
          setHeightRange={setHeightRange}
          marketValueFilterType={marketValueFilterType}
          setMarketValueFilterType={setMarketValueFilterType}
          marketValueRangeAll={marketValueRangeAll}
          setMarketValueRangeAll={setMarketValueRangeAll}
          marketValueRangeLow={marketValueRangeLow}
          setMarketValueRangeLow={setMarketValueRangeLow}
          marketValueRangeHigh={marketValueRangeHigh}
          setMarketValueRangeHigh={setMarketValueRangeHigh}
          hasReportsFilter={hasReportsFilter}
          setHasReportsFilter={setHasReportsFilter}
          availablePositions={availablePositions}
          availableLeagues={availableLeagues}
          setCurrentPage={setCurrentPage}
          onLoadData={loadContractData}
          citizenshipFilter={citizenshipFilter}
          setCitizenshipFilter={setCitizenshipFilter}
          footFilter={footFilter}
          setFootFilter={setFootFilter}
          isEuFilter={isEuFilter}
          setIsEuFilter={setIsEuFilter}
          countryOfBirthFilter={countryOfBirthFilter}
          setCountryOfBirthFilter={setCountryOfBirthFilter}
          currentClubFilter={currentClubFilter}
          setCurrentClubFilter={setCurrentClubFilter}
          contractExpiresFrom={contractExpiresFrom}
          setContractExpiresFrom={setContractExpiresFrom}
          contractExpiresTo={contractExpiresTo}
          setContractExpiresTo={setContractExpiresTo}
          playerAgentFilter={playerAgentFilter}
          setPlayerAgentFilter={setPlayerAgentFilter}
          minGoals={minGoals}
          setMinGoals={setMinGoals}
          minAssists={minAssists}
          setMinAssists={setMinAssists}
          minMatchesPlayed={minMatchesPlayed}
          setMinMatchesPlayed={setMinMatchesPlayed}
        />

        {/* View Controls and Results Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Header with view controls */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-bold text-gray-800">
                  {t('contracts.playersFound')}
                </h2>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <BarChart3 className="w-4 h-4 mr-1" />
                  {totalCount} total
                </span>
              </div>
              
              <div className="flex items-center gap-4">
                {/* View Mode Toggle */}
                <div className="flex items-center bg-gray-200 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === 'cards'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === 'table'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Items per page */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Mostrar:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                
                {/* Export button */}
                <button
                  onClick={exportToCSV}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar
                </button>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <LoadingSpinner />
                <p className="mt-4 text-gray-600 text-lg">Cargando datos de contratos...</p>
                <p className="mt-2 text-gray-500 text-sm">Analizando oportunidades de fichaje</p>
              </div>
            ) : players.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertTriangle className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No se encontraron jugadores</h3>
                <p className="text-gray-600 mb-6">Intenta ajustar los filtros para ver más resultados</p>
                
                {(filter !== 'all' || positionFilter !== 'all' || leagueFilter !== 'all' || searchTerm || !showExpired || hasReportsFilter || 
                  citizenshipFilter || footFilter !== 'all' || isEuFilter !== null || countryOfBirthFilter || currentClubFilter ||
                  contractExpiresFrom || contractExpiresTo || playerAgentFilter || minGoals > 0 || minAssists > 0 || minMatchesPlayed > 0) && (
                  <button
                    onClick={() => {
                      setFilter('all');
                      setPositionFilter('all');
                      setLeagueFilter('all');
                      setSearchTerm('');
                      setShowExpired(false);
                      setHasReportsFilter(false);
                      setMarketValueFilterType('low');
                      setMarketValueRangeAll([0, 200]);
                      setMarketValueRangeLow([0, 10]);
                      setMarketValueRangeHigh([10, 200]);
                      setAgeRange([15, 40]);
                      setHeightRange([1.50, 2.10]);
                      // Reset advanced filters
                      setCitizenshipFilter('');
                      setFootFilter('all');
                      setIsEuFilter(null);
                      setCountryOfBirthFilter('');
                      setCurrentClubFilter('');
                      setContractExpiresFrom('');
                      setContractExpiresTo('');
                      setPlayerAgentFilter('');
                      // Reset performance filters
                      setMinGoals(0);
                      setMinAssists(0);
                      setMinMatchesPlayed(0);
                    }}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    <Filter className="w-5 h-5 mr-2" />
                    Limpiar todos los filtros
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Cards View */}
                {viewMode === 'cards' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 auto-rows-fr mb-8">
                    {players.map((player, index) => {
                      // Create unique key from multiple identifiers
                      const uniqueKey = `${player.player_id || ''}-${player.id_fbref || ''}-${player.id_transfermarkt || ''}-${index}`;
                      return (
                        <div
                          key={uniqueKey}
                          style={{ animationDelay: `${index * 50}ms` }}
                          className="animate-fade-in h-full"
                        >
                          <ContractCard player={player} />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Table View */}
                {viewMode === 'table' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th 
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('nombre_transfermarkt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Jugador</span>
                              {getSortIcon('nombre_transfermarkt')}
                            </div>
                          </th>
                          <th 
                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('edad_transfermarkt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Edad</span>
                              {getSortIcon('edad_transfermarkt')}
                            </div>
                          </th>
                          <th 
                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('posicion_transfermarkt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>{t('contracts.position')}</span>
                              {getSortIcon('posicion_transfermarkt')}
                            </div>
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Pie
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Altura
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Nacionalidad
                          </th>
                          <th 
                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('equipo_transfermarkt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>{t('contracts.team')}</span>
                              {getSortIcon('equipo_transfermarkt')}
                            </div>
                          </th>
                          <th 
                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('competicion')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>{t('contracts.league')}</span>
                              {getSortIcon('competicion')}
                            </div>
                          </th>
                          <th 
                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('fecha_fin_contrato')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>{t('contracts.contractEnd')}</span>
                              {getSortIcon('fecha_fin_contrato')}
                            </div>
                          </th>
                          <th 
                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('dias_restantes')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>{t('contracts.status')}</span>
                              {getSortIcon('dias_restantes')}
                            </div>
                          </th>
                          <th 
                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('valor_mercado')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>{t('contracts.value')}</span>
                              {getSortIcon('valor_mercado')}
                            </div>
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            G/A
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Partidos
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Agente
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('contracts.actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {players.map((player, index) => {
                          // Remove "(id)" format from player names
                          const rawPlayerName = player.player_name || player.nombre_transfermarkt || t('contracts.unknownPlayer');
                          const playerName = rawPlayerName.split('(')[0].trim() || rawPlayerName;
                          const playerPosition = translatePosition(player.position || player.posicion_transfermarkt || 'N/A', currentLang);
                          const playerTeam = player.current_club_name || player.equipo_transfermarkt || 'N/A';
                          const playerAge = player.date_of_birth 
                            ? new Date().getFullYear() - new Date(player.date_of_birth).getFullYear()
                            : player.edad_transfermarkt || 0;
                          const playerImage = player.player_image_url || player.url_foto_jugador || 'https://images.pexels.com/photos/1618200/pexels-photo-1618200.jpeg';
                          const playerId = player.player_id || player.id_fbref;
                          const contractDate = player.contract_expires || player.fecha_fin_contrato;
                          
                          // New fields
                          const playerFoot = translateFoot(player.foot || player.pie_habil || 'N/A', currentLang);
                          const playerHeight = player.height || (player.altura ? `${player.altura} cm` : 'N/A');
                          const playerNationality = player.citizenship || player.nacion || player.country_of_birth || 'N/A';
                          const playerAgent = player.player_agent_name || player.player_agent || 'N/A';
                          const goalsAssists = `${player.goles || 0}/${player.asistencias || 0}`;
                          const matchesPlayed = player.partidos_jugados || player.partidos || 0;
                          
                          // Create unique key from multiple identifiers
                          const uniqueKey = `${player.player_id || ''}-${player.id_fbref || ''}-${player.id_transfermarkt || ''}-${index}`;
                          
                          return (
                            <tr key={uniqueKey} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="relative">
                                    <img
                                      src={playerImage}
                                      alt={playerName}
                                      className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = 'https://images.pexels.com/photos/1618200/pexels-photo-1618200.jpeg';
                                      }}
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <Link
                                      to={`/sporting/player/${playerId}`}
                                      className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline transition-colors block truncate"
                                    >
                                      {playerName}
                                    </Link>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{playerAge}</td>
                              <td className="px-3 py-3 text-sm text-gray-700">{playerPosition}</td>
                              <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{playerFoot}</td>
                              <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{playerHeight}</td>
                              <td className="px-3 py-3 text-sm text-gray-700">{playerNationality}</td>
                              <td className="px-3 py-3 text-sm text-gray-700">{playerTeam}</td>
                              <td className="px-3 py-3 text-sm text-gray-700">{player.competicion}</td>
                              <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">
                                {contractDate ? new Date(contractDate).toLocaleDateString('es-ES') : 'N/A'}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getUrgencyColor(player.dias_restantes)}`}>
                                  {getUrgencyIcon(player.dias_restantes)}
                                  <span className="ml-1">
                                    {player.dias_restantes < 0
                                      ? t('contracts.expiredDaysAgo', { days: Math.abs(player.dias_restantes) })
                                      : getContractPeriod(player.dias_restantes) === 'this_season'
                                      ? t('contracts.thisSeason')
                                      : getContractPeriod(player.dias_restantes) === 'one_year'
                                      ? t('contracts.oneYear')
                                      : t('contracts.longTerm')}
                                  </span>
                                </span>
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap font-medium">
                                €{((player.valor_mercado || 0) / 1000000).toFixed(1)}M
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap text-center">
                                <span className="font-medium">{goalsAssists}</span>
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap text-center">
                                {matchesPlayed}
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-600 truncate max-w-[150px]" title={playerAgent}>
                                {playerAgent}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <Link
                                  to={`/sporting/player/${playerId}`}
                                  className="text-sm text-blue-600 font-medium hover:underline transition-colors flex items-center"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Ver
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalCount)} de {totalCount} resultados
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Anterior
                    </button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      Siguiente
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractMonitorPage;